// src/pages/Sanitary/SanitaryDailyClosingPage.jsx
// =============================================================================
// Sanitary automated daily closing settings and run history
// =============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  fetchSanitaryDailyClosingRuns,
  fetchSanitaryDailyClosingSettings,
  runSanitaryDailyClosing,
  updateSanitaryDailyClosingSettings,
} from "../../services/sanitaryDailyClosingService";

import "../Stock/StockMasterDataPage.css";
import "./SanitaryProductsPage.css";

const todayIso = () => new Date().toISOString().slice(0, 10);

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function runStatusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "stock-md-pill";
  if (s === "completed_with_warnings" || s === "skipped") return "stock-md-pill stock-md-pill--warning";
  if (s === "failed") return "stock-md-pill stock-md-pill--danger";
  return "stock-md-pill stock-md-pill--muted";
}

export default function SanitaryDailyClosingPage() {
  const { t } = useTranslation(["sanitary", "common"]);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    enabled: false,
    scheduler_mode: "manual_only",
    delay_days: 2,
    run_time: "02:00",
    timezone: "Europe/Warsaw",
    auto_post_stock: true,
    catch_up_days: 1,
  });
  const [runs, setRuns] = useState([]);
  const [manualDate, setManualDate] = useState(todayIso());
  const [force, setForce] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const nextTarget = useMemo(() => {
    if (!form.delay_days) return "—";
    const d = new Date();
    d.setDate(d.getDate() - Number(form.delay_days || 0));
    return d.toISOString().slice(0, 10);
  }, [form.delay_days]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [loadedSettings, loadedRuns] = await Promise.all([
        fetchSanitaryDailyClosingSettings(),
        fetchSanitaryDailyClosingRuns({ limit: 30 }),
      ]);
      setSettings(loadedSettings);
      setForm({
        enabled: Boolean(loadedSettings?.enabled),
        scheduler_mode: loadedSettings?.scheduler_mode || "manual_only",
        delay_days: loadedSettings?.delay_days ?? 2,
        run_time: loadedSettings?.run_time || "02:00",
        timezone: loadedSettings?.timezone || "Europe/Warsaw",
        auto_post_stock: loadedSettings?.auto_post_stock !== false,
        catch_up_days: loadedSettings?.catch_up_days ?? 1,
      });
      setRuns(Array.isArray(loadedRuns) ? loadedRuns : []);
    } catch (err) {
      setError(t("sanitary:dailyClosing.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const updateField = (field, value) => {
    setError("");
    setSuccess("");
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const saved = await updateSanitaryDailyClosingSettings(form);
      setSettings(saved);
      setSuccess(t("sanitary:dailyClosing.messages.saved"));
      await loadData();
    } catch (err) {
      setError(t("sanitary:dailyClosing.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const runManual = async () => {
    setRunning(true);
    setError("");
    setSuccess("");
    try {
      const result = await runSanitaryDailyClosing({ closing_date: manualDate, force, trigger_source: "manual" });
      if (result?.skipped) {
        setSuccess(t(`sanitary:dailyClosing.messages.skipped.${result.reason}`, { defaultValue: t("sanitary:dailyClosing.messages.skipped.generic") }));
      } else {
        setSuccess(t("sanitary:dailyClosing.messages.runStarted"));
      }
      await loadData();
    } catch (err) {
      setError(t("sanitary:dailyClosing.messages.runFailed"));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="stock-md-page sanitary-page">
      <div className="stock-md-header">
        <div>
          <div className="stock-md-eyebrow">{t("sanitary:dailyClosing.eyebrow")}</div>
          <h1>{t("sanitary:dailyClosing.title")}</h1>
          <p>{t("sanitary:dailyClosing.subtitle")}</p>
        </div>
      </div>

      {error && <div className="stock-md-error">{error}</div>}
      {success && <div className="stock-md-success">{success}</div>}
      <div className="stock-md-info-box">{t("sanitary:dailyClosing.guidance")}</div>

      <div className="stock-md-grid stock-md-grid--top">
        <section className="stock-md-card">
          <div className="stock-md-card-header">
            <div>
              <h2>{t("sanitary:dailyClosing.settings.title")}</h2>
              <p>{t("sanitary:dailyClosing.settings.subtitle")}</p>
            </div>
          </div>

          <form className="stock-md-form" onSubmit={saveSettings}>
            <label className="stock-md-check-row">
              <input
                type="checkbox"
                checked={Boolean(form.enabled)}
                onChange={(e) => updateField("enabled", e.target.checked)}
              />
              <span>{t("sanitary:dailyClosing.fields.enabled")}</span>
            </label>

            <label className="stock-md-field">
              {t("sanitary:dailyClosing.fields.schedulerMode")}
              <select value={form.scheduler_mode} onChange={(e) => updateField("scheduler_mode", e.target.value)}>
                <option value="manual_only">{t("sanitary:dailyClosing.modes.manual_only")}</option>
                <option value="local">{t("sanitary:dailyClosing.modes.local")}</option>
                <option value="bullmq">{t("sanitary:dailyClosing.modes.bullmq")}</option>
                <option value="disabled">{t("sanitary:dailyClosing.modes.disabled")}</option>
              </select>
            </label>

            <div className="stock-md-grid stock-md-grid--two">
              <label className="stock-md-field">
                {t("sanitary:dailyClosing.fields.delayDays")}
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={form.delay_days}
                  onChange={(e) => updateField("delay_days", e.target.value)}
                />
              </label>
              <label className="stock-md-field">
                {t("sanitary:dailyClosing.fields.runTime")}
                <input type="time" value={form.run_time} onChange={(e) => updateField("run_time", e.target.value)} />
              </label>
            </div>

            <div className="stock-md-grid stock-md-grid--two">
              <label className="stock-md-field">
                {t("sanitary:dailyClosing.fields.timezone")}
                <input value={form.timezone} onChange={(e) => updateField("timezone", e.target.value)} />
              </label>
              <label className="stock-md-field">
                {t("sanitary:dailyClosing.fields.catchUpDays")}
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={form.catch_up_days}
                  onChange={(e) => updateField("catch_up_days", e.target.value)}
                />
              </label>
            </div>

            <label className="stock-md-check-row">
              <input
                type="checkbox"
                checked={Boolean(form.auto_post_stock)}
                onChange={(e) => updateField("auto_post_stock", e.target.checked)}
              />
              <span>{t("sanitary:dailyClosing.fields.autoPostStock")}</span>
            </label>

            <div className="stock-md-info-box">
              {t("sanitary:dailyClosing.settings.nextTarget", { date: nextTarget })}
            </div>

            <button type="submit" className="stock-md-button" disabled={saving || loading}>
              {saving ? t("common:common.loading") : t("common:actions.save")}
            </button>
          </form>
        </section>

        <section className="stock-md-card">
          <div className="stock-md-card-header">
            <div>
              <h2>{t("sanitary:dailyClosing.manual.title")}</h2>
              <p>{t("sanitary:dailyClosing.manual.subtitle")}</p>
            </div>
          </div>

          <div className="stock-md-form">
            <label className="stock-md-field">
              {t("sanitary:dailyClosing.fields.closingDate")}
              <input type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} />
            </label>
            <label className="stock-md-check-row">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
              <span>{t("sanitary:dailyClosing.fields.force")}</span>
            </label>
            <button type="button" className="stock-md-button" onClick={runManual} disabled={running || !manualDate}>
              {running ? t("sanitary:dailyClosing.messages.running") : t("sanitary:dailyClosing.actions.runManual")}
            </button>
          </div>
        </section>
      </div>

      <section className="stock-md-card">
        <div className="stock-md-card-header">
          <div>
            <h2>{t("sanitary:dailyClosing.runs.title")}</h2>
            <p>{t("sanitary:dailyClosing.runs.subtitle")}</p>
          </div>
          <button type="button" className="stock-md-button stock-md-button--secondary" onClick={loadData} disabled={loading}>
            {t("common:actions.refresh")}
          </button>
        </div>

        <div className="stock-md-table-wrap">
          <table className="stock-md-table stock-md-table--compact">
            <thead>
              <tr>
                <th>{t("sanitary:dailyClosing.runs.table.date")}</th>
                <th>{t("sanitary:dailyClosing.runs.table.status")}</th>
                <th>{t("sanitary:dailyClosing.runs.table.source")}</th>
                <th>{t("sanitary:dailyClosing.runs.table.started")}</th>
                <th>{t("sanitary:dailyClosing.runs.table.finished")}</th>
                <th>{t("sanitary:dailyClosing.runs.table.summary")}</th>
                <th>{t("sanitary:dailyClosing.runs.table.error")}</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td className="stock-md-empty" colSpan="7">
                    {loading ? t("common:common.loading") : t("sanitary:dailyClosing.runs.empty")}
                  </td>
                </tr>
              ) : (
                runs.map((run) => (
                  <tr key={run.id}>
                    <td>{run.closing_date}</td>
                    <td><span className={runStatusClass(run.status)}>{t(`sanitary:dailyClosing.statuses.${run.status}`, { defaultValue: run.status })}</span></td>
                    <td>{t(`sanitary:dailyClosing.triggers.${run.trigger_source}`, { defaultValue: run.trigger_source })}</td>
                    <td>{formatDateTime(run.started_at || run.createdAt)}</td>
                    <td>{formatDateTime(run.finished_at)}</td>
                    <td>
                      {t("sanitary:dailyClosing.runs.summaryText", {
                        posted: run.posted_count || 0,
                        corrections: run.correction_count || 0,
                        warnings: run.warning_count || 0,
                        errors: run.error_count || 0,
                      })}
                      {run.summary_json?.calculation_mode && (
                        <div className="stock-md-muted">{run.summary_json.calculation_mode}</div>
                      )}
                      {Number(run.summary_json?.working_solution_prepared_automatically || 0) > 0 && (
                        <div className="stock-md-muted">{t("sanitary:dailyClosing.runs.autoSolutions", { count: Number(run.summary_json.working_solution_prepared_automatically || 0) })}</div>
                      )}
                      {Number(run.summary_json?.working_solution_manual_preparation_required || 0) > 0 && (
                        <div className="stock-md-muted">{t("sanitary:dailyClosing.runs.manualSolutions", { count: Number(run.summary_json.working_solution_manual_preparation_required || 0) })}</div>
                      )}
                    </td>
                    <td>{run.error_message || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
